import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  DocumentCheckIcon,
  CheckCircleIcon,
  EyeIcon,
  GlobeAltIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ArrowsPointingInIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ duration: 0.7, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.95 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.6, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */
const capabilities = [
  {
    icon: EyeIcon,
    title: 'Marketing Compliance Monitoring',
    description:
      'Every ad, email, social post, and disclosure checked against federal regulatory rules — Reg Z, UDAAP, Reg DD, ECOA, Fair Housing Act, and more. AI-powered detection with deterministic rules-engine verification and mandatory human approval workflows. UDAAP 3-dimensional risk scoring (Unfair / Deceptive / Abusive, 0–100). Immutable, examiner-ready audit trail with SHA-256 integrity hashing on every check.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'HMDA LAR Builder & Validation',
    description:
      'Upload, validate, clean, and prepare LAR data for submission. 170+ CFPB edit checks across syntactical, validity, quality, and macro-quality categories — each with a plain-language AI explanation. Field-mapping templates for LOS imports, geocoding validation, data-quality scoring, and submission-ready exports. A multi-day manual HMDA review becomes a same-day deliverable.',
  },
  {
    icon: ScaleIcon,
    title: 'Fair Lending Analysis',
    description:
      'A full IFLEP-aligned Fair Lending Dashboard with 21 risk factors across underwriting, pricing, steering, redlining, and marketing. Denial-rate and pricing-disparity analysis with chi-square, Fisher\'s exact, and Welch\'s t-test significance testing. 9 prohibited-basis groups, 5 loan-purpose segments. Census-tract mapping with demographic overlays, regulator-correct majority-minority tract classification, and content screening for prohibited-basis language, steering, and discouragement.',
  },
  {
    icon: ChartBarIcon,
    title: 'Regression Analysis',
    description:
      'Logistic regression for denial and steering models. OLS regression for pricing-disparity analysis. Matched-pair analysis for examiner file review. Regulator-aware output formatted to FDIC, OCC, Fed, and NCUA examination standards, with full methodology documentation produced automatically — not manually assembled — for examiner defensibility.',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'CRA Module',
    description:
      'Assessment-area delineation with census-tract demographics. Lending Test analysis (geographic + borrower-income distribution) pulling from HMDA, small business, small farm, and consumer origination data. Community-development activity tracking across loans, investments, grants, and services. Size-adaptive framework (Small / Intermediate / Large), CRA health scoring, gap analysis for underserved geographies, and exam-readiness assessment.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Regulatory Change Intelligence & Exam Packaging',
    description:
      'Automated monitoring of enforcement actions and regulatory updates from CFPB, OCC, and FDIC — with deduplication, high-value-action flagging, compliance-area tagging, and priority assignment. One-click examiner-ready ZIP exports and board reports that pull from every module: fair lending findings, HMDA data quality, marketing compliance, CRA performance, and regression results, all aggregated with methodology documentation.',
  },
];

const interconnectivityFlows = [
  {
    title: 'HMDA LAR Data Flows Into Fair Lending',
    description: 'LAR entries are enriched with 50+ derived columns and fed directly into denial-rate, pricing, and geographic-disparity analyses — no re-upload, no re-entry. One data upload, multiple analytical outputs.',
  },
  {
    title: 'Fair Lending Findings Feed Regression',
    description: 'Statistical disparities surfaced in the Fair Lending Dashboard are validated through logistic and OLS regression, adding the multivariate controls that strengthen examiner defensibility.',
  },
  {
    title: 'CRA Lending Analysis Uses HMDA Originations',
    description: 'CRA lending tests pull origination volumes and geographic distribution from the same LAR data, with assessment-area mapping and income-level classification — no separate data prep.',
  },
  {
    title: 'Marketing Compliance Informs Fair Lending Risk',
    description: 'Content findings — prohibited-basis language, steering, discouragement — flow into the fair lending and redlining risk picture, connecting what a bank says to how it lends.',
  },
  {
    title: 'Everything Rolls Into Exam & Board Packages',
    description: 'Marketing compliance stats, fair lending findings, CRA health scores, regression results, and regulatory alerts aggregate into a single examiner-ready export and an executive board report. One click.',
  },
];

const clientScenarios = [
  {
    icon: ShieldCheckIcon,
    title: 'Compliance Program Assessments',
    description:
      'When Cathy conducts a compliance program review, Clarid provides the technology layer that operationalizes her recommendations. She can leave each client with a system that continuously monitors compliance posture and flags gaps — turning a point-in-time assessment into ongoing execution between engagements.',
  },
  {
    icon: ScaleIcon,
    title: 'Mock Examinations & Remediation',
    description:
      'Clarid generates the same analytical outputs examiners evaluate — HMDA data-quality scores, fair lending statistical tests, denial-rate disparities, marketing compliance audit trails. Cathy can use these outputs during mock exams and hand clients a monitoring program that sustains the fixes long after the engagement ends.',
  },
  {
    icon: UserGroupIcon,
    title: 'Regulatory Action Response',
    description:
      'For clients responding to consent orders, MRAs, or examiner findings, Clarid provides documented evidence of corrective action — continuous monitoring, automated testing, and examiner-ready reports that demonstrate sustained improvement, not just a one-time fix.',
  },
  {
    icon: ChartBarIcon,
    title: 'Fair Lending & CRA Analytics',
    description:
      'Statistical testing, regression analysis, redlining screens, and CRA evaluation that would take days to assemble manually — delivered same-week with full methodology documentation. Cathy spends her time on interpretation and client guidance; Clarid handles the computational work underneath.',
  },
];

const partnershipModels = [
  {
    num: '01',
    title: 'Analytical Engine Inside Engagements',
    description:
      'Cathy uses Clarid as the analytical platform during compliance assessments, fair lending reviews, HMDA data-integrity testing, and CRA evaluations. The technology handles data processing and statistical analysis; Cathy handles interpretation, client communication, and strategic guidance. Faster delivery, deeper analysis, the same trusted advisory relationship.',
  },
  {
    num: '02',
    title: 'Referral & Co-Sell',
    description:
      'Cathy recommends Clarid to banking and credit-union clients who need continuous compliance monitoring. Clarid handles the technology relationship; Cathy keeps the advisory relationship — and is seen as the expert who connected them with best-in-class technology. Revenue share on the subscription term, structured to keep Cathy firmly in the advisory role.',
  },
  {
    num: '03',
    title: 'Continuous Monitoring That Retains Clients',
    description:
      'Engagements end; the compliance program keeps running. After Cathy\'s work, the client deploys Clarid for ongoing monitoring that maintains the standards she established — automated testing, continuous risk scoring, and examiner-ready documentation produced as a byproduct of normal operations. Cathy stays connected as the advisory partner between engagements.',
  },
  {
    num: '04',
    title: 'Co-Branded, Productized Deliverables',
    description:
      'Package Clarid into defined deliverables — for example, a "Fair Lending Review" or "HMDA & CRA Readiness Audit, powered by Clarid analytics." Co-branded reporting and a shared go-to-market give Cathy a productized offering that\'s easier for client banks to buy and easier for a solo practice to scale without adding headcount.',
  },
];

const dueDiligenceDocs = [
  'AI Governance Policy',
  'FS AI RMF Gap Analysis (control objectives mapped, 100% coverage)',
  'Model Inventory with validation framework',
  'Bias Testing Methodology & Results',
  'Data Flow Architecture Documentation',
  'Information Security Program (WISP)',
  'Incident Response Plan',
  'Business Continuity Plan',
  'Vendor Inventory (third-party dependencies)',
];

const nextSteps = [
  { num: 1, title: 'This Conversation', description: "Explore alignment between Cathy's compliance consulting practice and Clarid's AI-native platform — where the analytical engine extends what she already delivers." },
  { num: 2, title: 'Platform Walkthrough', description: 'A live demo across HMDA validation, fair lending statistical analysis, CRA evaluation, marketing compliance monitoring, and one-click exam packaging.' },
  { num: 3, title: 'Joint Client Pilot', description: 'Identify one or two of Cathy\'s clients for a structured pilot with defined success criteria — Cathy provides the advisory expertise and relationship, Clarid provides the analytical engine and onboarding support.' },
  { num: 4, title: 'Partnership Formalization', description: 'Define the model — analytical engine, referral & co-sell, continuous monitoring, co-branded deliverables, or a combination — and the commercial structure that fits an independent practice.' },
];

/* ─── Component ─── */
export default function KleinPartnerPage() {
  useEffect(() => {
    document.title = 'Cathy Klein + Clarid — AI-Native Compliance for Community Banks';

    const metaTags: Record<string, string> = {
      'og:title': 'Cathy Klein + Clarid — Strategic Partnership',
      'og:description': 'Independent compliance expertise meets an AI-native compliance platform. Built by a community bank CMO, with partners from the FDIC and Federal Reserve.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Cathy Klein + Clarid Partnership',
      'twitter:description': 'Expert compliance advisory, amplified by AI-native technology.',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Minimal Header ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <span className="text-xl font-display font-bold text-navy-900 tracking-tight">Cathy Klein</span>
            <span className="text-slate-300 text-xl font-light">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
          </div>
        </div>
      </header>

      {/* ─── Section 1: Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-600 mb-8">
              Strategic Partnership
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              Expert Advisory, Amplified by AI-Native Compliance
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.65rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Cathy Klein brings deep compliance expertise and trusted relationships with the
              community banks and credit unions she advises. Clarid brings the AI-native platform
              that operationalizes expert recommendations into continuous, automated monitoring —
              HMDA validation, fair lending statistical analysis, CRA evaluation, regression modeling,
              and one-click exam packaging. Together: advisory depth with technology that sustains it.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 2: The Compliance Gap ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                The Gap Between Expert Guidance and Daily Execution
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Community banks and credit unions bring in an expert consultant for good reason —
                  program assessments, mock examinations, policy remediation, and regulatory response
                  all require deep domain expertise. But the challenge usually isn't the quality of the
                  guidance. It's the gap between strategic recommendations and the daily operational
                  execution that keeps a compliance program running between engagements.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Institutions with lean compliance teams — often one to three people managing HMDA,
                  fair lending, marketing compliance, CRA, and regulatory change simultaneously — need
                  technology that automates the execution layer. Not another portal to log into. A system
                  that actually does the work, continuously, with examiner-ready outputs.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  And there's a parallel pressure on the advisory side. The analytical work underneath an
                  assessment — pulling LAR data, running denial-rate and pricing comparisons, computing
                  statistical significance, documenting methodology — is time-consuming when done by hand,
                  and it limits how many clients an independent practice can serve at depth. Clarid removes
                  that bottleneck.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  Cathy has the expertise to identify the gaps. Clarid has the technology to close them — continuously.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Platform Capabilities ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-6">
                What Clarid Does
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
                One platform across the full compliance program — built so each module reinforces the others.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {capabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 group-hover:scale-110 transition-all duration-300">
                      <cap.icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.4}>
            <p className="mt-14 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid integrates into the tools bankers already use — Microsoft Copilot, Claude, ChatGPT —
              through MCP (Model Context Protocol). No new portal to learn. Compliance intelligence embedded
              in daily workflows.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 4: Cross-Module Interconnectivity ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-300 mb-5">
                The Differentiator
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                Cross-Module Interconnectivity
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                Most compliance tools are siloed — HMDA validation in one system, fair lending analysis in
                another, marketing compliance somewhere else, board reporting in a spreadsheet. Clarid
                connects them. Marketing compliance findings flow into fair lending risk assessments. HMDA
                data feeds directly into CRA lending tests and regression analysis. Exam packages and board
                reports pull from every module automatically. No one else connects the compliance program
                this way.
              </p>
            </div>
          </FadeUp>

          <div className="space-y-5 lg:space-y-6 mt-14 max-w-5xl mx-auto">
            {interconnectivityFlows.map((flow, i) => (
              <ScaleIn key={flow.title} delay={i * 0.08}>
                <div className="p-8 lg:p-10 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500">
                  <div className="flex items-center gap-4 mb-3">
                    <ArrowsPointingInIcon className="h-6 w-6 text-teal-400 flex-shrink-0" />
                    <h3 className="text-xl font-display font-bold text-white">{flow.title}</h3>
                  </div>
                  <p className="text-lg text-slate-400 leading-relaxed pl-10">{flow.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.5}>
            <p className="mt-14 text-center text-lg text-slate-400 max-w-4xl mx-auto leading-relaxed">
              This interconnectivity means a single data upload triggers analysis across multiple compliance
              domains — and the results surface together in exam packages and board reports. For a consultant,
              it means the technology layer reinforces every recommendation across the entire compliance
              program, not just one piece of it.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 5: How Cathy's Clients Benefit ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-8">
                How Cathy's Clients Benefit
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 max-w-5xl mx-auto leading-relaxed">
                Clarid complements the work Cathy already does — adding a continuous technology layer that
                operationalizes her recommendations and keeps them running.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {clientScenarios.map((scenario, i) => (
              <ScaleIn key={scenario.title} delay={i * 0.08}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 group-hover:scale-110 transition-all duration-300">
                      <scenario.icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{scenario.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{scenario.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 6: Examination Perspective ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
              Built With the Examination Perspective
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Partners from the FDIC and Federal Reserve
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  A former FDIC Supervisory Examiner and a former Federal Reserve Supervisory Examiner and
                  fair lending subject-matter expert — with decades of combined examination experience across
                  hundreds of community-bank examinations — are partners in Clarid. Every rule engine, every
                  statistical test, every output format is validated against what examiners actually evaluate.
                </p>
              </div>
            </ScaleIn>

            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Built by the Buyer
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  Clarid's founder is a community-bank CMO who manages marketing compliance, HMDA reporting,
                  and examiner relationships as part of his daily work. This platform wasn't designed by
                  engineers guessing at compliance needs — it was built by someone who lives them and who
                  sits across from examiners using the outputs Clarid generates.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── Section 7: Partnership Models ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                How This Works Together
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
                There's no single right way for Cathy and Clarid to combine. The models below are designed to
                be mixed, sequenced, or chosen based on what fits the practice today.
              </p>
            </FadeUp>

            <div className="space-y-6 lg:space-y-8">
              {partnershipModels.map((model, i) => (
                <ScaleIn key={model.num} delay={i * 0.1}>
                  <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 shadow-sm hover:border-teal-200 hover:bg-white hover:shadow-xl transition-all duration-500">
                    <div className="flex items-start gap-6">
                      <span className="text-3xl font-display font-bold text-teal-600/60 flex-shrink-0 mt-0.5">
                        {model.num}
                      </span>
                      <div>
                        <h3 className="text-2xl font-display font-bold text-navy-900 mb-3">{model.title}</h3>
                        <p className="text-lg text-slate-500 leading-relaxed">{model.description}</p>
                      </div>
                    </div>
                  </div>
                </ScaleIn>
              ))}
            </div>

            <FadeUp delay={0.4}>
              <p className="mt-12 text-center text-lg text-slate-500 italic">
                We'd welcome a conversation about which model — or combination — aligns best with how Cathy
                wants to grow her practice.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 8: Due Diligence ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                We've Done the Homework
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-14 leading-relaxed">
                Clarid maintains a comprehensive vendor due-diligence package designed to meet the
                expectations in interagency guidance on third-party relationships. Available on request:
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="max-w-3xl mx-auto">
                <ul className="space-y-5">
                  {dueDiligenceDocs.map((doc, i) => (
                    <FadeIn key={doc} delay={0.15 + i * 0.05}>
                      <li className="flex items-start gap-4">
                        <CheckCircleIcon className="h-6 w-6 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-600">{doc}</span>
                      </li>
                    </FadeIn>
                  ))}
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.6}>
              <p className="mt-12 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
                Our Executive Due Diligence Summary, Data Flow Architecture, and full FS AI RMF gap analysis
                are available for review. SOC 2 Type I is on our 12-month roadmap — we're documenting controls now.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 9: Moving Forward ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
                Moving Forward
              </h2>
            </FadeUp>

            <div className="space-y-10 lg:space-y-12">
              {nextSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-navy-900 text-white flex items-center justify-center text-lg font-bold">
                      {step.num}
                    </div>
                    <div className="pt-2">
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{step.title}</h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 10: Contact ─── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder &amp; CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-teal-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-teal-700 transition-colors">
                  bobby@clarid.ai
                </a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="text-slate-400 font-display font-semibold text-sm">Cathy Klein</span>
            <span className="text-slate-200">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
          </div>
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid. Confidential.
          </p>
        </div>
      </footer>
    </div>
  );
}
